'use client';
import type { ComponentPropsWithoutRef } from 'react';
import { Controller, type FieldPath, type FieldValues, useFormContext } from 'react-hook-form';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/slw/shared/ui/base-form';
import { Textarea as BaseTextarea } from '@/slw/shared/ui/textarea';
import { cn } from '@/slw/shared/lib/utils';

// ----------------------------------------------------------------------

interface CustomRHFTextareaProps<TFieldValues extends FieldValues>
  extends Omit<ComponentPropsWithoutRef<typeof Controller>, 'name' | 'control' | 'render'>,
    Omit<ComponentPropsWithoutRef<typeof BaseTextarea>, 'name' | 'defaultValue'> {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: string;
  label?: string;
  withError?: boolean;
  showMessError?: boolean;
  direction?: 'row' | 'column';
  required?: boolean;
  labelClass?: string;
}

export function Textarea<TFieldValues extends FieldValues>({
  name,
  label,
  helperText,
  defaultValue,
  showMessError = true,
  containerClass,
  className,
  withError = true,
  direction = 'row',
  required,
  labelClass,
  ...other
}: CustomRHFTextareaProps<TFieldValues>) {
  const { control } = useFormContext();
  const directionClass =
    direction === 'row' ? 'gap-2 items-center flex space-y-0' : 'gap-1 space-y-1 flex flex-col items-start';

  return (
    <FormField
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <FormItem className={cn(directionClass, containerClass)}>
          {!!label && (
            <FormLabel className={cn('whitespace-nowrap font-normal text-sm leading-[1.5]', labelClass)}>
              {label}
              {required && <span className='text-[#dc2626]'>*</span>}
            </FormLabel>
          )}
          <FormControl>
            <BaseTextarea
              {...field}
              {...other}
              value={field.value}
              onChange={(event) => {
                field.onChange(event);
              }}
              className={cn(className, error && withError && 'border-destructive focus-visible:ring-destructive')}
            />
          </FormControl>
          {helperText && withError && showMessError && (
            <p className='text-sm text-muted-foreground ml-1.5'>{helperText}</p>
          )}
          {error && withError && showMessError && <FormMessage>{error.message}</FormMessage>}
        </FormItem>
      )}
    />
  );
}
